import { setAccount } from './boardNav';
import { clearAllDrafts } from './drafts';
import type { LoginOptions, ResolvedBranding } from '@kaba/shared';
import { useQuery } from '@tanstack/react-query';
import type { Me } from '@kaba/shared';
import { api, ApiError } from './api';
import { queryClient } from './board';
import { startOffline, wipeOffline } from './offline';
import { outbox } from './outbox';
import { startOutbox } from './offlineSync';

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        const me = await api.get<Me>('/api/me');
        setAccount(me.id);
        // Offline lesen und ausstehende Änderungen dieses Kontos (0.44)
        if (!me.isGuest) startOffline(queryClient, me.id);
        void startOutbox(me.id);
        return me;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          setAccount(null);
          // Sitzung abgelaufen: zwischengespeicherte Daten weg, nicht übertragene Änderungen bleiben für die nächste Anmeldung
          void wipeOffline(undefined, false);
          return null;
        }
        // Ohne Verbindung: mit dem zuletzt bekannten Konto weiterarbeiten (Stand vom Gerät)
        const cached = queryClient.getQueryData<Me | null>(['me']);
        if (cached && err instanceof ApiError && err.status === 0) {
          setAccount(cached.id);
          void startOutbox(cached.id);
          return cached;
        }
        throw err;
      }
    },
    staleTime: 5 * 60_000,
  });
}

export interface PublicConfig {
  instanceName: string;
  signup: 'open' | 'invite';
  mailEnabled: boolean;
  version: string;
  maxUploadMb: number;
  needsSetup: boolean;
  login?: LoginOptions;
  /** Installationsbranding (öffentlich) */
  branding?: ResolvedBranding;
}

export function usePublicConfig() {
  return useQuery({ queryKey: ['config'], queryFn: () => api.get<PublicConfig>('/api/config'), staleTime: 5 * 60_000 });
}

export async function signIn(email: string, password: string) {
  await api.post('/api/auth/sign-in/email', { email, password, rememberMe: true });
  await queryClient.invalidateQueries({ queryKey: ['me'] });
}

export async function signUp(name: string, email: string, password: string) {
  await api.post('/api/auth/sign-up/email', { name, email, password });
  await queryClient.invalidateQueries({ queryKey: ['me'] });
}

export async function completeSetup(input: { key: string; name: string; email: string; password: string }) {
  await api.post('/api/setup', input);
  await queryClient.invalidateQueries({ queryKey: ['config'] });
  await queryClient.invalidateQueries({ queryKey: ['me'] });
}

export async function signOut(returnTo = '/') {
  const pending = outbox().getSnapshot().length;
  if (pending && !window.confirm(`${pending === 1 ? 'Eine Änderung ist' : `${pending} Änderungen sind`} noch nicht übertragen und ${pending === 1 ? 'geht' : 'gehen'} beim Abmelden verloren. Trotzdem abmelden?`)) return;
  await api.post('/api/auth/sign-out', {});
  // Daten dieses Kontos auf dem Gerät löschen (offline Stand und Ablage)
  await wipeOffline(outbox().account ?? undefined, true);
  queryClient.clear();
  clearAllDrafts(); // Entwürfe gehören zur abgemeldeten Person
  location.href = returnTo;
}

export async function requestPasswordReset(email: string) {
  await api.post('/api/auth/request-password-reset', { email, redirectTo: '/reset-password' });
}

export async function resetPassword(token: string, newPassword: string) {
  await api.post('/api/auth/reset-password', { token, newPassword });
}
